/**
 * Shared label generation utilities for reporting tables
 *
 * SINGLE SOURCE OF TRUTH for all label generation, type suffix stripping,
 * field prefix handling, and field grouping across the tio-reporting addon.
 *
 * Used by:
 * - OpenSearchFieldMapper.formatFieldLabel (delegates here)
 * - OpenSearchFieldMapper.getFieldGroup (delegates here)
 * - OpenSearchFieldMapper.removeFieldTypeSuffixes (delegates here)
 * - Table components for column headers
 */

// =============================================================================
// Type Suffix Stripping
// =============================================================================

/**
 * Strip OpenSearch type suffixes from field names.
 * Handles .keyword dot notation and camelCase type indicators.
 *
 * @example
 * stripTypeSuffixes('employeeNameKeyword') // 'employeeName'
 * stripTypeSuffixes('createdAtDate') // 'createdAt'
 * stripTypeSuffixes('name.keyword') // 'name'
 * stripTypeSuffixes('isActiveBoolean') // 'isActive'
 */
export const stripTypeSuffixes = (fieldName: string): string => {
  return fieldName.replace(
    /[_.]?(Keyword|Text|Date|DateTime|Time|Boolean|Numeric|Integer|Long|Float|Double|Short|Byte|Nested|Analyzable|Flattened|FlatObject)$/i,
    ''
  );
};

// =============================================================================
// Domain Prefix Handling
// =============================================================================

/** Domain prefix patterns ordered by specificity (most specific first) */
const DOMAIN_PREFIX_CONTEXT: { pattern: RegExp; context: string }[] = [
  // TAS
  { pattern: /^tas[_]?program[_]?template[_]?field/i, context: 'Program' },
  { pattern: /^tas[_]?program[_]?instance[_]?field/i, context: 'Program' },
  { pattern: /^tas[_]?program[_]?instance/i, context: 'Program' },
  { pattern: /^tas[_]?program[_]?template/i, context: 'Program' },
  { pattern: /^tas[_]?application[_]?field/i, context: 'Application' },
  { pattern: /^tas[_]?application/i, context: 'Application' },
  { pattern: /^tas[_]?course[_]?field/i, context: 'Course' },
  { pattern: /^tas[_]?course/i, context: 'Course' },
  { pattern: /^tas/i, context: '' },

  // PSLF
  { pattern: /^pslf[_]?form[_]?fields?/i, context: 'PSLF' },
  { pattern: /^pslf[_]?form/i, context: 'PSLF' },
  { pattern: /^pslf[_]?employer/i, context: 'PSLF' },
  { pattern: /^pslf/i, context: 'PSLF' },

  // SYF
  { pattern: /^syf[_]?match[_]?participant[_]?fields?/i, context: 'SYF' },
  { pattern: /^syf[_]?match[_]?participant/i, context: 'SYF' },
  { pattern: /^syf[_]?employee[_]?ef[_]?fields?/i, context: 'SYF' },
  { pattern: /^syf/i, context: 'SYF' },

  // SLR
  { pattern: /^slr[_]?contribution[_]?plan[_]?participant[_]?fields?/i, context: 'SLR' },
  { pattern: /^slr[_]?contribution[_]?plan[_]?participant/i, context: 'SLR' },
  { pattern: /^slr[_]?contribution/i, context: 'SLR' },
  { pattern: /^slr/i, context: 'SLR' },

  // Person / Company / All (generic index prefixes)
  { pattern: /^person[_]?/i, context: 'Person' },
  { pattern: /^company[_]?/i, context: 'Company' },
  { pattern: /^all[_]?/i, context: '' },
];

/** Strip domain prefixes from a field name, returning only the base name */
const stripDomainPrefixes = (fieldName: string): string => {
  let result = fieldName;
  for (const { pattern } of DOMAIN_PREFIX_CONTEXT) {
    if (pattern.test(fieldName)) {
      result = fieldName.replace(pattern, '');
      break;
    }
  }

  // Strip 'Custom' or 'custom_fields' from the resulting base name
  // Must be careful not to strip 'Customer' => use lookahead for capital or underscore or end
  result = result.replace(/^(?:[Cc]ustom)(?:_?[Ff]ields?)?(?=[A-Z_]|$)/, '');

  // Strip any leading underscores left by prefix removals
  return result.replace(/^_+/, '');
};

/** Detect entity context (e.g. 'Program', 'Application', 'PSLF') from a prefixed field name */
const detectEntityContext = (fieldName: string): string => {
  for (const { pattern, context } of DOMAIN_PREFIX_CONTEXT) {
    if (pattern.test(fieldName)) {
      return context;
    }
  }
  return '';
};

// =============================================================================
// Special Label Mappings
// =============================================================================

/**
 * Custom label mappings for fields that don't follow standard naming conventions.
 * Keys are lowercase for case-insensitive lookup.
 * Checked at different stages by different functions:
 * - generateSmartLabel checks full key (e.g., 'paymentLast') before prefix stripping
 * - generateColumnLabel checks stripped key (e.g., 'last') after prefix stripping
 */
const SPECIAL_LABEL_MAP: Record<string, string> = {
  paymentlast: 'Date',
  paymentfirst: 'Date',
  last: 'Date',
  first: 'Date',
};

// =============================================================================
// Field Grouping
// =============================================================================

/**
 * Check whether a field name looks like a custom/EF field.
 * Used by getDisplayGroup to append "/ Custom" suffix.
 */
export const isCustomLikeField = (fieldName: string): boolean => {
  const normalized = fieldName.toLowerCase();
  return (
    /(^|[._])custom([._]|$)/.test(normalized) ||
    /custom_fields|customfields/.test(normalized) ||
    /(^|\.)employee_ef_fields|(^|\.)employeeeffields/.test(normalized)
  );
};

// --- Cross-cutting field matchers (shared between model-specific and generic paths) ---

const isReviewField = (normalized: string) =>
  /(review|queue|assign|approval|approver)/.test(normalized);

// NOTE: Intentionally excludes bare "asset/assets" (matches total_assets, asset_value),
// bare "document/documents" (matches document_type), "blob", and "storage" (too broad).
// Use digital_asset or attachment-specific terms instead.
const isDigitalAssetOrAttachmentField = (normalized: string) =>
  /(attachment|attachments|digital_asset|digitalasset|evidence_file|uploaded_file|file_name|file_url|fax)/.test(
    normalized
  );

const isCompanySettingField = (normalized: string) =>
  /(company|partner).*(setting|settings|config|configuration|policy|preference|feature|enabled|disable|flag|rule)/.test(
    normalized
  ) ||
  /(setting|settings|config|configuration|policy|preference|feature|enabled|disable|flag|rule).*(company|partner)/.test(
    normalized
  );

// NOTE: Intentionally excludes bare "builder" (too broad — matches query_builder, form_builder).
// Require program_template or template_builder prefix for specificity.
const isProgramBuilderField = (normalized: string) =>
  /(program_template|programtemplate|template_builder|templatebuilder).*(setting|settings|config|configuration|policy|limit|rule|workflow|voucher)/.test(
    normalized
  ) ||
  /(setting|settings|config|configuration|policy|limit|rule|workflow|voucher).*(program_template|programtemplate|template_builder|templatebuilder)/.test(
    normalized
  );

// --- Model-specific grouping (dataset-aware) ---

const getPersonFieldGroup = (normalized: string): string | undefined => {
  if (/(company|employer|organization)/.test(normalized)) return 'Company';
  if (/(payroll|employee_id|employeeid|external_id|uuid|identifier|ssn)/.test(normalized)) {
    return 'Identifiers';
  }
  if (/(email|phone|mobile|contact)/.test(normalized)) return 'Contact Information';
  if (/(address|street|city|state|province|postal|zip|country)/.test(normalized)) {
    return 'Postal Address';
  }
  if (
    /(job|title|department|manager|employment|hire|termination|location|cost_center|cohort)/.test(
      normalized
    )
  ) {
    return 'Employment';
  }
  return 'Employee';
};

const getPslfFormFieldGroup = (normalized: string): string | undefined => {
  if (
    /(employer|organization|workspace|agency|company|ein|fein|address|approver|supervisor|hr)/.test(
      normalized
    )
  ) {
    return 'Employer Fields';
  }
  if (/(borrower|employee|person|applicant|name|email|phone)/.test(normalized)) {
    return 'Borrower Fields';
  }
  if (/(eligib|qualified|qualification|certif)/.test(normalized)) return 'Eligibility';
  if (/(payment|loan|balance|amount)/.test(normalized)) return 'Loans';
  if (/(form|status|submitted|certif|review|tracking|deadline)/.test(normalized)) return 'Form';
  return 'PSLF';
};

const getCompanyFieldGroup = (normalized: string): string | undefined => {
  if (/lpn|lnp|learning.?partner/.test(normalized)) return 'LNP / Learning Partner';
  if (isCompanySettingField(normalized)) return 'Company Settings';
  if (/(^|[_.])(id|uuid|identifier|company_id|partner_id)([_.]|$)/.test(normalized)) {
    return 'Identifiers';
  }
  if (/(email|phone|contact)/.test(normalized)) return 'Contact Information';
  if (/(address|street|city|state|province|postal|zip|country)/.test(normalized)) {
    return 'Postal Address';
  }
  if (/(launch|start|end|created|updated|date|fiscal)/.test(normalized)) return 'Lifecycle';
  return 'Company';
};

const getMatchParticipantFieldGroup = (normalized: string): string | undefined => {
  if (/(company|employer|sponsor)/.test(normalized)) return 'Company';
  if (/(eligib|qualified|qualification)/.test(normalized)) return 'Eligibility';
  if (
    /(setting|config|period|assignment|window|cycle|schedule|eligib|rule|effective|start|end|tier|amount|balance)/.test(
      normalized
    )
  ) {
    return 'Plan Settings & Assignments';
  }
  if (/(person|employee|participant|name|email|phone)/.test(normalized)) return 'Participant';
  return 'SYF';
};

const getEmployeePlanFieldGroup = (normalized: string): string | undefined => {
  if (/(company|employer)/.test(normalized)) return 'Company';
  if (/(eligib|qualified|qualification)/.test(normalized)) return 'Eligibility';
  if (
    /(setting|config|period|assignment|window|cycle|schedule|eligib|rule|effective|start|end|tier|amount|balance)/.test(
      normalized
    )
  ) {
    return 'Plan Settings & Assignments';
  }
  if (/(person|employee|participant|name|email|phone)/.test(normalized)) return 'Participant';
  return 'SLR';
};

const getTasProgramTemplateFieldGroup = (normalized: string): string | undefined => {
  if (/lpn|lnp|learning.?partner/.test(normalized)) return 'LNP / Learning Partner';
  if (isCompanySettingField(normalized)) return 'Company Settings';
  if (isProgramBuilderField(normalized)) return 'Program Builder Setup';
  if (/(company|partner)/.test(normalized)) return 'Company';
  if (/(name|type|program|template|voucher|policy|setting|limit)/.test(normalized)) {
    return 'Program Template';
  }
  return 'Program Details';
};

/** Map of modelType → model-specific grouping function */
const MODEL_GROUP_FN: Record<string, (normalized: string) => string | undefined> = {
  Person: getPersonFieldGroup,
  PslfForm: getPslfFormFieldGroup,
  Company: getCompanyFieldGroup,
  MatchParticipant: getMatchParticipantFieldGroup,
  EmployeePlan: getEmployeePlanFieldGroup,
  TasProgramTemplate: getTasProgramTemplateFieldGroup,
};

/**
 * Get the group/category for a field name (for organizing column selection UI).
 * Handles both camelCase (API response) and snake_case (OpenSearch index) field names.
 *
 * When `modelType` is provided (e.g. 'Person', 'PslfForm', 'Company'), uses
 * dataset-specific grouping rules that produce richer categories like
 * 'Contact Information', 'Postal Address', 'Employer Fields', etc.
 *
 * When `modelType` is omitted, falls back to generic prefix-based matching
 * (suitable for the legacy mapping code path).
 *
 * @example
 * getFieldGroup('tasProgramTemplateFieldProgramNameKeyword') // 'Program Details'
 * getFieldGroup('email', 'Person') // 'Contact Information'
 * getFieldGroup('employer_ein', 'PslfForm') // 'Employer Fields'
 * getFieldGroup('paymentAmountInteger') // 'Payments'
 */
export const getFieldGroup = (fieldName: string, modelType?: string): string => {
  const normalized = fieldName.toLowerCase();

  // --- Cross-cutting rules (apply regardless of modelType) ---

  // Employee EF / custom fields (most specific — before generic employee match)
  if (/^employee_ef_fields|^employee_custom_fields/i.test(fieldName)) return 'Employee';
  if (/^employeeEfFields|^employeeCustomFields/i.test(fieldName)) return 'Employee';

  // Digital asset / attachment fields that are also review fields → Review & Queue
  if (isDigitalAssetOrAttachmentField(normalized) && isReviewField(normalized)) {
    return 'Review & Queue';
  }
  if (isDigitalAssetOrAttachmentField(normalized)) return 'Digital Assets & Attachments';

  // LNP / Learning Partner (high priority, applies across all datasets)
  if (/lpn|lnp|learning.?partner/.test(normalized)) return 'LNP / Learning Partner';

  // Company settings & program builder setup (cross-cutting)
  if (isCompanySettingField(normalized)) return 'Company Settings';
  if (isProgramBuilderField(normalized)) return 'Program Builder Setup';

  // --- Model-specific grouping (when dataset context is available) ---
  if (modelType) {
    const modelFn = MODEL_GROUP_FN[modelType];
    if (modelFn) {
      const group = modelFn(normalized);
      if (group) return group;
    }
  }

  // --- Generic prefix-based matching (legacy / no modelType) ---

  // Specific domain groups (most specific first, BEFORE generic prefix matching)
  if (
    /tas_app_evidence|evidence_approval_fields|tasAppEvidence|evidenceApproval/i.test(fieldName)
  ) {
    return 'Evidence';
  }
  if (/tas_app_courses|courses_fields|courses_object|tasCourse/i.test(fieldName)) {
    return 'Courses';
  }
  if (/tas_app_field|tasApplicationField/i.test(fieldName)) return 'Application Fields';
  if (/scholarship/i.test(fieldName)) return 'Scholarships';
  if (/assistance/i.test(fieldName)) return 'Assistance';
  if (/evidence|evidence_approval/i.test(fieldName)) return 'Evidence';
  if (/eligibility/i.test(fieldName)) return 'Eligibility';
  if (/participant/i.test(fieldName)) return 'Participant';
  if (/tas_pi_field|tasProgramInstanceField/i.test(fieldName)) return 'Program Details';
  if (/tas_pi_lpn|lpn|learning.?partner/i.test(fieldName)) return 'LNP / Learning Partner';
  if (/tas_pi_withdrawal|withdrawal/i.test(fieldName)) return 'Withdrawal';
  if (/review|queue|assign/i.test(fieldName)) return 'Review & Queue';

  // camelCase prefix matching
  if (/^tasProgramTemplateField/i.test(fieldName)) return 'Program Details';
  if (/^tasProgramInstance/i.test(fieldName)) return 'Program Instance';
  if (/^tasProgramTemplate/i.test(fieldName)) return 'Program Template';
  if (/^tasApplication/i.test(fieldName)) return 'Application';
  if (/^tasCourse/i.test(fieldName)) return 'Courses';

  // Domain prefix matching (handles camelCase and snake_case)
  if (/^pslf/i.test(fieldName)) return 'PSLF';
  if (/^syf/i.test(fieldName)) return 'SYF';
  if (/^slr/i.test(fieldName)) return 'SLR';

  // snake_case prefix matching (for OpenSearch field names)
  if (/^tas_program_template_field/i.test(fieldName)) return 'Program Details';
  if (/^tas_program_instance/i.test(fieldName)) return 'Program Instance';
  if (/^tas_program_template/i.test(fieldName)) return 'Program Template';
  if (/^tas_application/i.test(fieldName)) return 'Application';
  if (/^tas_course/i.test(fieldName)) return 'Courses';

  // Entity / substring matching (fallback)
  if (/payment/i.test(fieldName)) return 'Payments';
  if (fieldName.includes('employee')) return 'Employee';
  if (fieldName.includes('program_template')) return 'Program Template';
  if (fieldName.includes('program_instance') || fieldName.includes('tas_program')) {
    return 'Program Instance';
  }
  if (fieldName.includes('application')) return 'Application';
  if (fieldName.includes('course')) return 'Courses';
  if (fieldName.includes('scholarship')) return 'Scholarships';
  if (fieldName.includes('assistance')) return 'Assistance';
  if (fieldName.includes('company')) return 'Company';
  if (/employee/i.test(fieldName)) return 'Employee';
  if (/company/i.test(fieldName)) return 'Company';

  // Generic substring matching (last resort)
  if (/program/i.test(fieldName)) return 'Program';
  if (/application/i.test(fieldName)) return 'Application';
  if (/course/i.test(fieldName)) return 'Courses';

  return 'Other';
};

/**
 * Get the display group for a field, appending "/ Custom" suffix when appropriate.
 * Wraps getFieldGroup with custom-field detection.
 *
 * @param fieldName - The field name to group
 * @param modelType - Optional model type for dataset-specific grouping
 * @param isCustomField - Whether the field is explicitly marked as custom
 */
export const getDisplayGroup = (
  fieldName: string,
  modelType?: string,
  isCustomField?: boolean
): string => {
  const baseGroup = getFieldGroup(fieldName, modelType);
  const isCustom = Boolean(isCustomField) || isCustomLikeField(fieldName);
  if (!isCustom) {
    return baseGroup;
  }
  return `${baseGroup} / Custom`;
};

// =============================================================================
// Label Generation
// =============================================================================

/** Fields that are common across entities and need context to disambiguate */
const AMBIGUOUS_FIELDS = [
  'id',
  'state',
  'name',
  'createdat',
  'created',
  'updatedat',
  'updated',
  'status',
  'type',
];

/**
 * Auto-generates human-readable labels from field names.
 * Handles both camelCase and snake_case input, converts to Title Case.
 *
 * @example
 * autoGenerateLabel('employeeName') // 'Employee Name'
 * autoGenerateLabel('employee_name') // 'Employee Name'
 * autoGenerateLabel('id') // 'Id'
 */
export const autoGenerateLabel = (fieldName: string) => {
  // Handle camelCase: insert spaces before capital letters
  let spaced = fieldName.replace(/([A-Z])/g, ' $1').trim();

  // Handle snake_case and kebab-case: replace separators with spaces
  spaced = spaced.replace(/[_-]/g, ' ');

  // Capitalize first letter of each word
  return spaced
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Generates smart labels for columns that keep necessary context
 * to disambiguate similar fields (e.g., "Program: Name" vs "Application: Name")
 *
 * Removes TAS metadata suffixes and prefixes, then adds back context for
 * ambiguous field names (id, state, name, etc.)
 *
 * @example
 * generateSmartLabel('tasProgramInstanceFieldNameKeyword')
 * // Returns: 'Program: Name' (stripped prefix and Keyword suffix, added context)
 *
 * generateSmartLabel('employeeNameKeyword')
 * // Returns: 'Employee Name' (stripped Keyword suffix, detected ambiguous 'Name', added context)
 *
 * generateSmartLabel('tasApplicationFieldCoursesBeginDateDate')
 * // Returns: 'Courses Begin Date' (stripped prefix and type suffixes, not ambiguous)
 */
export const generateSmartLabel = (columnKey: string) => {
  const cleanKey = stripTypeSuffixes(columnKey);

  // Special case mappings (checked before prefix stripping)
  const specialLabel = SPECIAL_LABEL_MAP[cleanKey.toLowerCase()];
  if (specialLabel) {
    return specialLabel;
  }

  // Employee EF fields: strip the "employee_" prefix from sub-keys
  // e.g., "employee_ef_fields.employee_email_keyword" → "Email"
  // The group header "EMPLOYEE FIELDS" provides the entity context
  const efMatch = cleanKey.match(
    /(?:^|\.)(?:employee_ef_fields|employeeEfFields|employee_custom_fields(?:_flattened)?|employeeCustomFields(?:Flattened)?)\.(.+)$/i
  );
  if (efMatch) {
    const subKeyMatch = efMatch[1];
    if (!subKeyMatch) {
      return autoGenerateLabel(cleanKey);
    }
    // Deeply nested EF fields might still have dots
    const parts = subKeyMatch.split('.').filter(Boolean);
    const leafSubKey = parts.at(-1) ?? subKeyMatch;
    // Strip leading "employee_" or "employee" prefix from sub-key
    const subKey = leafSubKey.replace(/^employee_/i, '').replace(/^employee(?=[A-Z])/i, '');
    return autoGenerateLabel(subKey);
  }

  // Handle dot notation (nested fields) - extract the leaf node
  const parts = cleanKey.split('.').filter(Boolean);
  const leafKey = parts.at(-1) ?? cleanKey;

  // Strip TAS prefixes to get the base field name
  const baseName = stripDomainPrefixes(leafKey);

  // Payment fields always get "Payment: " prefix for consistency
  if (cleanKey.toLowerCase().startsWith('payment')) {
    const paymentBaseName = baseName.replace(/^payment/i, '');
    return 'Payment: ' + autoGenerateLabel(paymentBaseName);
  }

  // Ambiguous fields need entity context to disambiguate
  const isAmbiguous = AMBIGUOUS_FIELDS.some((field) => baseName.toLowerCase() === field);

  if (isAmbiguous) {
    const preferredAmbiguousLabel =
      baseName.toLowerCase() === 'state' ? 'Status' : autoGenerateLabel(baseName);

    const context = detectEntityContext(cleanKey);
    if (context) {
      return context + ': ' + preferredAmbiguousLabel;
    }
    // Check for employee context (not a TAS prefix)
    if (/employee/i.test(cleanKey)) {
      return 'Employee: ' + preferredAmbiguousLabel;
    }
  }

  // Non-ambiguous fields use clean label without any prefix
  return autoGenerateLabel(baseName);
};

/**
 * Generate label for column selector UI — strips all prefixes, no entity context.
 * Used by OpenSearchFieldMapper.formatFieldLabel (default mode).
 *
 * @example
 * generateColumnLabel('tasProgramTemplateFieldProgramNameKeyword') // 'Program Name'
 * generateColumnLabel('tasApplicationStateKeyword') // 'State'
 * generateColumnLabel('paymentAmountInteger') // 'Amount'
 * generateColumnLabel('paymentLastDate') // 'Date'
 */
export const generateColumnLabel = (fieldName: string): string => {
  const clean = stripTypeSuffixes(fieldName);

  // Employee EF fields: strip the "employee_" prefix from sub-keys
  const efMatch = clean.match(
    /(?:^|\.)(?:employee_ef_fields|employeeEfFields|employee_custom_fields(?:_flattened)?|employeeCustomFields(?:Flattened)?)\.(.+)$/i
  );
  if (efMatch) {
    const [, rawSubKey] = efMatch;
    if (!rawSubKey) {
      return autoGenerateLabel(clean);
    }
    const parts = rawSubKey.split('.').filter(Boolean);
    const leafSubKey = parts.at(-1) ?? rawSubKey;
    const subKey = leafSubKey.replace(/^employee_/i, '').replace(/^employee(?=[A-Z])/i, '');
    return autoGenerateLabel(subKey);
  }

  // Handle dot notation (nested fields) - extract the leaf node
  const cleanParts = clean.split('.').filter(Boolean);
  const cleanLeaf = cleanParts.at(-1) ?? clean;

  // Strip TAS prefixes, then payment/field prefixes
  let stripped = stripDomainPrefixes(cleanLeaf);
  stripped = stripped.replace(/^payment/i, '').replace(/^field/i, '');

  // Check for special label mapping (e.g., 'last' → 'Date')
  const specialLabel = SPECIAL_LABEL_MAP[stripped.toLowerCase()];
  if (specialLabel) return specialLabel;

  return autoGenerateLabel(stripped);
};

/**
 * Generate label with entity context prefix — adds "Program: ", "Application: " etc.
 * Used by OpenSearchFieldMapper.formatFieldLabel with keepContext=true.
 *
 * Always adds context prefix for TAS-prefixed fields (unlike generateSmartLabel
 * which only adds context for ambiguous field names).
 *
 * @example
 * generateContextualLabel('tasProgramTemplateFieldProgramNameKeyword') // 'Program: Program Name'
 * generateContextualLabel('tasApplicationStateKeyword') // 'Application: State'
 * generateContextualLabel('tasCourseNameKeyword') // 'Course: Name'
 */
export const generateContextualLabel = (fieldName: string): string => {
  const clean = stripTypeSuffixes(fieldName);

  // Extract leaf node for processing, but keep full clean key for context detection
  const cleanParts = clean.split('.').filter(Boolean);
  const leafNode = cleanParts.at(-1) ?? clean;

  let contextPrefix = '';
  // Start baseName with the leaf node
  let baseName = leafNode;

  // Detect context from TAS prefixes (using the original clean key, because leaf may not have it)
  const entityContext = detectEntityContext(clean);
  if (entityContext) {
    contextPrefix = entityContext + ': ';
    baseName = stripDomainPrefixes(leafNode);
  } else if (/^payment/i.test(clean)) {
    contextPrefix = 'Payment: ';
    baseName = leafNode.replace(/^payment/i, '') || leafNode;
  } else {
    // For fields without explicit prefixes, check for ambiguous names using the leaf
    const isAmbiguous = AMBIGUOUS_FIELDS.some((f) => leafNode.toLowerCase() === f);

    if (isAmbiguous) {
      // Try to infer context from the original field name
      if (/program/i.test(fieldName)) {
        contextPrefix = 'Program: ';
      } else if (/application/i.test(fieldName)) {
        contextPrefix = 'Application: ';
      } else if (/course/i.test(fieldName)) {
        contextPrefix = 'Course: ';
      } else if (/payment/i.test(fieldName)) {
        contextPrefix = 'Payment: ';
      } else if (/employee/i.test(fieldName)) {
        contextPrefix = 'Employee: ';
      }
    }
  }

  // Check for special label mapping
  const specialLabel = SPECIAL_LABEL_MAP[baseName.toLowerCase()];
  if (specialLabel) {
    return contextPrefix + specialLabel;
  }

  if (contextPrefix && baseName.toLowerCase() === 'state') {
    return contextPrefix + 'Status';
  }

  return contextPrefix + autoGenerateLabel(baseName);
};
